import {
  batchCreateName,
  batchDeleteElementAndSubs,
  compareRect,
  CORRECTIONS_REVIEWED,
  createFieldValue,
  getMostRecentFieldValue,
  getNestedOrFloatingElementById,
  getNestedSubelementsByType,
  getNextUuid,
  INDEXED_INCORRECTLY,
} from '@fs/zion-sls'
import { FullNameApp } from '../../../../shared/services/config'

export const theSameRect =
  (rect, compareImageIdAsWell = false) =>
  (r) => {
    if (!r) return false // If the rect is null, don't compare
    return compareRect(rect, r, compareImageIdAsWell)
  }

const createNameWithRects = ({ nameSuperId, elementType, fieldType, text, rects, attribution, currentTask }) => {
  const nameFieldId = getNextUuid()
  const dispatches = batchCreateName({
    partitionKey: currentTask.partitionKey,
    nameSuperId,
    elementType,
    fieldType,
    fieldValue: createFieldValue(text, attribution),
    nameFieldId,
  })
  rects.forEach((rect) => {
    dispatches.push({
      type: 'ADD_RECT',
      value: {
        partitionKey: currentTask.partitionKey,
        id: nameFieldId,
        rect,
      },
    })
  })
  return dispatches
}

const syncRects = (nameField, rects, currentTask) => {
  const dispatches = []
  if (nameField && nameField.rects)
    nameField.rects.forEach((existingRect) => {
      if (!rects.find(theSameRect(existingRect, true))) {
        dispatches.push({
          type: 'REM_RECT',
          value: {
            id: nameField.id,
            rect: existingRect,
            partitionKey: currentTask.partitionKey,
          },
        })
      }
    })
  rects.forEach((desiredRect) => {
    if (nameField && !nameField.rects?.find(theSameRect(desiredRect, true))) {
      dispatches.push({
        type: 'ADD_RECT',
        value: {
          id: nameField.id,
          rect: desiredRect,
          partitionKey: currentTask.partitionKey,
        },
      })
    }
  })
  return dispatches
}

/**
 * Used for surname prefixes, matronymic, and patronymic names. It searches for an existing surname element on the task or a
 * shared element from one of the used fields. If it doesn't find an existing element, it seraches for a dispatch to create
 * a surname. If there is no dispatch, it creates the surname and field. If it finds a surname elment to use, it only creates
 * the field if that doesn't already exist.
 * @param dispatches
 * @param nameSuperId
 * @param nameValue
 * @param attribution
 * @param fieldType
 * @param currentTask
 * @param images
 * @returns {{removeDispatches: *[], dispatches: *[]}}
 */
const createSurnameOrSurnameElement = ({
  dispatches,
  nameSuperId,
  nameValue,
  attribution,
  fieldType,
  currentTask,
  images,
}) => {
  let surnameElement = currentTask.subElements.find((se) => se.elementType === 'NAME_SURNAME')
  let surnameElementId = surnameElement?.id
  const existingSurnameEl = surnameElement
  let usingSharedSuper = false
  let usingSharedSuperFromOtherStep = false
  const removeDispatches = []
  if (
    dispatches.find(
      (dispatch) =>
        dispatch.type === 'REM_SUB' &&
        dispatch.value.subId === surnameElementId &&
        dispatch.value.superId === currentTask.id
    )
  ) {
    // We used a shared surname in another step of the save
    const newSurnameId = dispatches.find(
      (dispatch) => dispatch.type === 'ADD_SUB' && dispatch.value.superId === currentTask.id
    )?.value.subId
    surnameElement = getNestedOrFloatingElementById(images, newSurnameId)
    surnameElementId = surnameElement?.id
    usingSharedSuperFromOtherStep = true
  }
  nameValue.fieldIds.forEach((fieldId) => {
    const field = getNestedOrFloatingElementById(images, fieldId)
    const nameSuper = getNestedOrFloatingElementById(images, field?.superElements?.[0]?.id)
    if (existingSurnameEl?.id !== nameSuper?.id) {
      if (!usingSharedSuperFromOtherStep && existingSurnameEl?.elementType === nameSuper?.elementType) {
        // Share this name with other tasks already using it
        usingSharedSuper = true
        surnameElement = nameSuper
        surnameElementId = nameSuper.id
      } else if (Array.isArray(field?.rects)) {
        // Remove the highlight from the old field
        field.rects.forEach((rect) => {
          if (nameValue.rects.find(theSameRect(rect, true))) {
            removeDispatches.push({
              type: 'REM_RECT',
              value: {
                partitionKey: field.partitionKey,
                id: field.id,
                rect,
              },
            })
          }
        })
      }
    }
  })
  if (!surnameElement) {
    const surnameElementCreate = dispatches.find(
      (dispatch) => dispatch.type === 'CREATE_ELEMENT' && dispatch.value.elementType === 'NAME_SURNAME'
    )
    surnameElementId = surnameElementCreate?.value.id
  }
  const fieldValue = createFieldValue(nameValue.text, attribution)
  let newDispatches = []
  if (!surnameElementId) {
    newDispatches = createNameWithRects({
      nameSuperId,
      elementType: 'NAME_SURNAME',
      fieldType,
      text: nameValue.text,
      rects: nameValue.rects,
      attribution,
      currentTask,
    })
  } else {
    const field = surnameElement?.subElements.find((se) => se.fieldType === fieldType)
    let nameFieldId = field?.id
    if (!field) {
      nameFieldId = getNextUuid()
      newDispatches = newDispatches.concat([
        {
          type: 'CREATE_ELEMENT',
          value: {
            id: nameFieldId,
            partitionKey: currentTask.partitionKey,
            elementType: 'FIELD',
            extraArgs: { fieldType },
          },
        },
        {
          type: 'ADD_SUB',
          value: {
            superPartitionKey: currentTask.partitionKey,
            subPartitionKey: currentTask.partitionKey,
            superId: surnameElementId,
            subId: nameFieldId,
            elementType: 'NAME_SURNAME',
          },
        },
      ])
    }
    if (usingSharedSuper) {
      dispatches.forEach((dispatch) => {
        // Rewrite any dispatches that affect the surname we are removing
        if (dispatch.value.id === existingSurnameEl.id) {
          dispatch.value.id = surnameElementId
        }
        if (dispatch.value.superId === existingSurnameEl.id) {
          dispatch.value.superId = surnameElementId
        }
      })
      if (
        !dispatches.find(
          (dispatch) =>
            dispatch.type === 'REM_SUB' &&
            dispatch.value.subId === surnameElementId &&
            dispatch.value.superId === currentTask.id
        )
      ) {
        // Add the shared name to the NAME element and remove any old name.
        newDispatches.push({
          type: 'ADD_SUB',
          value: {
            superPartitionKey: currentTask.partitionKey,
            subPartitionKey: currentTask.partitionKey,
            superId: currentTask.id,
            subId: surnameElementId,
            elementType: 'NAME',
          },
        })
        if (existingSurnameEl) {
          newDispatches.push({
            type: 'REM_SUB',
            value: {
              superPartitionKey: currentTask.partitionKey,
              subPartitionKey: currentTask.partitionKey,
              superId: currentTask.id,
              subId: existingSurnameEl.id,
              elementType: 'NAME',
            },
          })
        }
      }
      existingSurnameEl.subElements.forEach((existingField) => {
        if (
          !dispatches.find(
            (dispatch) =>
              dispatch.type === 'REM_SUB' &&
              dispatch.value.subId === existingField &&
              dispatch.value.superId === existingSurnameEl.id
          )
        ) {
          // Move existing fields to the shared surname
          newDispatches.push({
            type: 'REM_SUB',
            value: {
              superPartitionKey: currentTask.partitionKey,
              subPartitionKey: currentTask.partitionKey,
              superId: existingSurnameEl.id,
              subId: existingField.id,
              elementType: 'NAME_SURNAME',
            },
          })
          newDispatches.push({
            type: 'ADD_SUB',
            value: {
              superPartitionKey: currentTask.partitionKey,
              subPartitionKey: currentTask.partitionKey,
              superId: surnameElementId,
              subId: existingField.id,
              elementType: 'NAME_SURNAME',
            },
          })
        }
      })
    }
    newDispatches.push({
      type: 'ADD_FV',
      value: {
        fieldValue,
        id: nameFieldId,
        partitionKey: currentTask.partitionKey,
      },
    })
    if (field) {
      newDispatches = newDispatches.concat(syncRects(field, nameValue.rects, currentTask))
    } else {
      nameValue.rects.forEach((rect) => {
        newDispatches.push({
          type: 'ADD_RECT',
          value: {
            partitionKey: currentTask.partitionKey,
            id: nameFieldId,
            rect,
          },
        })
      })
    }
  }
  return { dispatches: newDispatches, removeDispatches }
}

const modifyOrCreateNameElement = ({
  elementType,
  fieldType,
  originalValue,
  text,
  rects,
  fieldIds,
  attribution,
  currentTask,
  images,
}) => {
  let dispatches = []
  let removeDispatches = []
  let usingSharedSuper = false
  let namePartEl = currentTask.subElements.find((se) => se.elementType === elementType)
  const existingNamePartEl = namePartEl
  fieldIds.forEach((fieldId) => {
    const field = getNestedOrFloatingElementById(images, fieldId)
    const nameSuper = getNestedOrFloatingElementById(images, field?.superElements?.[0]?.id)
    if (existingNamePartEl?.id !== nameSuper?.id) {
      if (existingNamePartEl?.elementType === nameSuper?.elementType) {
        // Share this name with other tasks already using it
        usingSharedSuper = true
        namePartEl = nameSuper
      } else if (Array.isArray(field?.rects)) {
        // Remove the highlight from the old field
        field.rects.filter(Boolean).forEach((rect) => {
          if (rects.find(theSameRect(rect, true))) {
            removeDispatches.push({
              type: 'REM_RECT',
              value: {
                partitionKey: field.partitionKey,
                id: field.id,
                rect,
              },
            })
          }
        })
      }
    }
  })

  if (text) {
    if (namePartEl) {
      const nameField = namePartEl.subElements.find((se) => se.fieldType === fieldType)
      let nameFieldId = nameField?.id
      if (!nameField) {
        nameFieldId = getNextUuid()
        dispatches = dispatches.concat([
          {
            type: 'CREATE_ELEMENT',
            value: {
              id: nameFieldId,
              partitionKey: currentTask.partitionKey,
              elementType: 'FIELD',
              extraArgs: { fieldType },
            },
          },
          {
            type: 'ADD_SUB',
            value: {
              superPartitionKey: currentTask.partitionKey,
              subPartitionKey: currentTask.partitionKey,
              superId: namePartEl.id,
              subId: nameFieldId,
              elementType,
            },
          },
        ])
      }
      if (usingSharedSuper) {
        // Add the shared name to the NAME element and remove any old name.
        dispatches.push({
          type: 'ADD_SUB',
          value: {
            superPartitionKey: currentTask.partitionKey,
            subPartitionKey: currentTask.partitionKey,
            superId: currentTask.id,
            subId: namePartEl.id,
            elementType: 'NAME',
          },
        })
        if (existingNamePartEl) {
          dispatches.push({
            type: 'REM_SUB',
            value: {
              superPartitionKey: currentTask.partitionKey,
              subPartitionKey: currentTask.partitionKey,
              superId: currentTask.id,
              subId: existingNamePartEl.id,
              elementType: 'NAME',
            },
          })
        }
      }
      dispatches.push({
        type: 'ADD_FV',
        value: {
          id: nameFieldId,
          partitionKey: currentTask.partitionKey,
          fieldValue: createFieldValue(text, attribution),
          originalValue,
        },
      })
      dispatches = dispatches.concat(syncRects(nameField, rects, currentTask))
    } else {
      dispatches = dispatches.concat(
        createNameWithRects({
          nameSuperId: currentTask.id,
          elementType,
          fieldType,
          text,
          originalValue,
          rects,
          attribution,
          currentTask,
        })
      )
    }
  } else if (namePartEl) {
    if (namePartEl.superElements?.length > 1) {
      dispatches.push({
        type: 'REM_SUB',
        value: {
          superPartitionKey: currentTask.partitionKey,
          subPartitionKey: currentTask.partitionKey,
          superId: currentTask.id,
          subId: namePartEl.id,
          elementType: 'NAME',
        },
      })
    } else {
      removeDispatches = batchDeleteElementAndSubs({ element: namePartEl })
    }
  }
  return { dispatches, removeDispatches }
}

const getReasonId = (fieldAfterReview, currentTask, fieldType) => {
  const fieldOnCurrentTask = getNestedSubelementsByType(currentTask, fieldType)[0]
  const fieldOnCurrentTaskValue = getMostRecentFieldValue(fieldOnCurrentTask) || ''
  // If nameValue has highlights, but there's no fieldOnCurrentTask then the user added a highlight to a new fieldType, effectively changing some text on the record
  const userAddedFieldToRecord = Boolean(!fieldOnCurrentTask && fieldAfterReview.rects.length)
  // If the nameValue no longer has highlights then the user made changes to the text on the field by means of removing the highlight from it
  const userRemovedFieldFromRecord = Boolean(fieldOnCurrentTask && !fieldAfterReview.rects.length)
  // If there is no text on fieldOnCurrentTask and nameValue has text, then the user added text
  const userAddedTextToField = Boolean(!fieldOnCurrentTaskValue && fieldAfterReview.text)
  // If the text on nameValue is different from that on taskField, then the user made changes to the text
  const userUpdatedTextOnField = Boolean(fieldAfterReview.text !== fieldOnCurrentTaskValue)
  if (userAddedFieldToRecord || userRemovedFieldFromRecord || userAddedTextToField || userUpdatedTextOnField) {
    return INDEXED_INCORRECTLY
  }
  return CORRECTIONS_REVIEWED
}

/**
 * Filter out BOTH ADD_RECT/REM_RECT pairs that cancel eachother out. We get a conflicting action error from SLS.  https://fhjira.churchofjesuschrist.org/browse/RIP-1918
 */
export const removeConflictingRectDispatches = (dispatches) => {
  const newDispatches = dispatches.filter((dispatch) => {
    if (dispatch.type === 'REM_RECT') {
      return !dispatches.find(
        (disp) =>
          disp.type === 'ADD_RECT' &&
          disp.value.id === dispatch.value.id &&
          compareRect(disp.value.rect, dispatch.value.rect, true)
      )
    }
    if (dispatch.type === 'ADD_RECT') {
      return !dispatches.find(
        (disp) =>
          disp.type === 'REM_RECT' &&
          disp.value.id === dispatch.value.id &&
          compareRect(disp.value.rect, dispatch.value.rect, true)
      )
    }
    return true
  })
  return newDispatches
}

/** Filter out any ADD_RECT that is too similar to another ADD_RECT, leaving only one of them
 * Or any REM_RECT that is too similar to another REM_RECT
 * These are duplicate actions, and they cause a duplicate action error in SLS. https://fhjira.churchofjesuschrist.org/browse/RIP-1906
 */
export const removeDuplicateRectDispatches = (dispatches) => {
  const newDispatches = []
  dispatches.forEach((dispatch) => {
    if (dispatch.type !== 'ADD_RECT' && dispatch.type !== 'REM_RECT') newDispatches.push(dispatch)
    if (dispatch.type === 'ADD_RECT') {
      const noDuplicateAddRectFound = Boolean(
        !newDispatches.find(
          (disp) =>
            disp.type === 'ADD_RECT' &&
            disp.value.id === dispatch.value.id &&
            compareRect(disp.value.rect, dispatch.value.rect, true)
        )
      )
      if (noDuplicateAddRectFound) newDispatches.push(dispatch)
    }
    if (dispatch.type === 'REM_RECT') {
      const noDuplicateRemRectFound = Boolean(
        !newDispatches.find(
          (disp) =>
            disp.type === 'REM_RECT' &&
            disp.value.id === dispatch.value.id &&
            compareRect(disp.value.rect, dispatch.value.rect, true)
        )
      )
      if (noDuplicateRemRectFound) newDispatches.push(dispatch)
    }
  })
  return newDispatches
}

/**
 * Processes a name element by modifying or creating it based on the provided parameters.
 *
 * @param {Object} nameValues - An object containing the name values.
 * @param {Object} currentTask - The current task context.
 * @param {Array} images - An array of images related to the task.
 * @param {string} elementType - The type of the element being processed.
 * @param {string} fieldType - The type of the field being processed.
 * @param {Object} attribution - An object containing attribution information.
 * @returns {Object} The modified or newly created name element.
 */
const processNameElement = (nameValues, currentTask, images, elementType, fieldType, attribution) => {
  return modifyOrCreateNameElement({
    elementType,
    fieldType,
    originalValue: nameValues[fieldType]?.originalValue,
    text: nameValues[fieldType].text,
    rects: nameValues[fieldType].rects,
    fieldIds: nameValues[fieldType].fieldIds,
    attribution: { ...attribution, reasonId: getReasonId(nameValues[fieldType], currentTask, fieldType) },
    currentTask,
    images,
  })
}

/**
 * Assembles dispatches and remove dispatches for name correction tasks.
 *
 * @param {Object} nameValues - The values of the names to be corrected.
 * @param {Object} currentTask - The current task containing the name correction details.
 * @param {Array} images - The images associated with the name correction task.
 * @returns {Object} An object containing two arrays: `dispatches` and `removeDispatches`.
 *
 * @typedef {Object} Dispatch
 * @property {string} type - The type of the dispatch.
 * @property {Object} value - The value associated with the dispatch.
 *
 * @typedef {Object} NameValue
 * @property {string} text - The text value of the name.
 *
 * @typedef {Object} Attribution
 * @property {string} comment - The comment for the attribution.
 * @property {string} reasonId - The reason ID for the attribution.
 * @property {string} app - The application name for the attribution.
 *
 * @callback ProcessNameElement
 * @param {Object} nameValues - The values of the names to be corrected.
 * @param {Object} currentTask - The current task containing the name correction details.
 * @param {Array} images - The images associated with the name correction task.
 * @param {string} elementType - The type of the name element.
 * @param {string} fieldType - The field type of the name element.
 * @param {Attribution} attribution - The attribution details.
 * @returns {Object} An object containing `dispatches` and `removeDispatches` arrays.
 *
 * @callback CreateSurnameOrSurnameElement
 * @param {Object} params - The parameters for creating surname or surname element.
 * @param {Array} params.dispatches - The current dispatches array.
 * @param {string} params.nameSuperId - The super ID of the name.
 * @param {NameValue} params.nameValue - The value of the name.
 * @param {Attribution} params.attribution - The attribution details.
 * @param {string} params.fieldType - The field type of the name element.
 * @param {Object} params.currentTask - The current task containing the name correction details.
 * @param {Array} params.images - The images associated with the name correction task.
 * @returns {Object} An object containing `dispatches` and `removeDispatches` arrays.
 */
const assembleDispatches = (nameValues, currentTask, images, addSubForAttribution = true) => {
  const nameSuperId = currentTask.id
  const attribution = {
    comment: '',
    reasonId: getReasonId(nameValues.NAME_GN, currentTask, 'NAME_GN'),
    app: FullNameApp,
  }
  let dispatches = []
  let removeDispatches = []

  const nameElements = [
    { elementType: 'NAME_GIVEN', fieldType: 'NAME_GN' },
    { elementType: 'NAME_SURNAME', fieldType: 'NAME_SURN' },
    { elementType: 'NAME_SUFFIX', fieldType: 'NAME_SUF' },
    { elementType: 'NAME_PREFIX', fieldType: 'NAME_PRE' },
  ]

  nameElements.forEach(({ elementType, fieldType }) => {
    const dispatchesResult = processNameElement(nameValues, currentTask, images, elementType, fieldType, attribution)
    dispatches = dispatches.concat(dispatchesResult.dispatches)
    removeDispatches = removeDispatches.concat(dispatchesResult.removeDispatches)
  })

  const surnameElements = [
    { fieldType: 'NAME_SPRE', nameValue: nameValues.NAME_SPRE },
    { fieldType: 'NAME_PATR', nameValue: nameValues.NAME_PATR },
    { fieldType: 'NAME_MATR', nameValue: nameValues.NAME_MATR },
  ]

  surnameElements.forEach(({ fieldType, nameValue }) => {
    if (nameValue.text) {
      const dispatchesResult = createSurnameOrSurnameElement({
        dispatches,
        nameSuperId,
        nameValue,
        attribution: { ...attribution, reasonId: getReasonId(nameValue, currentTask, fieldType) },
        fieldType,
        currentTask,
        images,
      })
      dispatches = dispatches.concat(dispatchesResult.dispatches)
      removeDispatches = removeDispatches.concat(dispatchesResult.removeDispatches)
    }
  })

  // Filter out REM_RECTS on fields that are being removed, such as when a given name field is moved to surname
  removeDispatches = removeDispatches.filter((dispatch) => {
    if (dispatch.type === 'REM_RECT') {
      return !removeDispatches.find((disp) => disp.type === 'REMOVE_ELEMENT' && disp.value.id === dispatch.value.id)
    }
    return true
  })

  // We don't have rules that disallow the invalid editing of rects. This is the next best thing.
  dispatches = removeConflictingRectDispatches(dispatches)
  dispatches = removeDuplicateRectDispatches(dispatches)
  removeDispatches = removeConflictingRectDispatches(removeDispatches)
  removeDispatches = removeDuplicateRectDispatches(removeDispatches)

  const attrDispatches = addNAMEAttribution(dispatches, removeDispatches, currentTask, addSubForAttribution)
  dispatches = dispatches.concat(attrDispatches)

  return { dispatches, removeDispatches }
}

/**
 * To have NAME elements counted by the backend as reviewed, we need to do an ADD_SUB of an existing name part that will trigger the backend to add attribution for the NAME element.
 * Yes, this seems weird, but this is what we were told to do by Eric Foutz.
 *
 * @param {Array} dispatches - Dispatches in payload. Used to make sure we aren't removing the element that we are doing the ADD_SUB.
 * @param {Array} removeDispatches - Need to make sure we are adding an ADD_SUB for an element that exists.
 * @param {Object} currentTask - Current Task.
 * @param {boolean} [addSubForAttribution=true] - Flag to determine if ADD_SUB should be added for attribution.
 * @returns {Array} Array of dispatches.
 */
export function addNAMEAttribution(dispatches, removeDispatches, currentTask, addSubForAttribution = true) {
  const attrDispatches = []
  if (!dispatches.find((dispatch) => dispatch.type === 'ADD_SUB' && dispatch.value.superId === currentTask.id)) {
    let i = 0
    let namePartEl = currentTask.subElements?.[i]
    // Make sure we are not removing the sub element that we are adding as a sub
    const findRemoveDispatchById = (dispatch) => isRemoveDispatchForNamePart(dispatch, namePartEl, currentTask)
    let namePartRemoveDispatch =
      removeDispatches.find(findRemoveDispatchById) || dispatches.find(findRemoveDispatchById)
    // If it is going to be removed, choose another name part to add again
    while (i < currentTask?.subElements?.length && namePartRemoveDispatch) {
      i++
      namePartEl = currentTask.subElements?.[i]
      namePartRemoveDispatch = removeDispatches.find(findRemoveDispatchById)
    }
    // Add an ADD_SUB action for existing subElement to update the attribution on the NAME element
    if (namePartEl && addSubForAttribution) {
      attrDispatches.push({
        type: 'ADD_SUB',
        value: {
          superPartitionKey: currentTask.partitionKey,
          subPartitionKey: namePartEl?.partitionKey,
          superId: currentTask.id,
          subId: namePartEl?.id,
          elementType: currentTask.elementType,
        },
      })
    }
  }
  return attrDispatches
}

/**
 * Checks if a dispatch is a remove dispatch for a name part.
 *
 * @param {Object} dispatch - The dispatch object to check.
 * @param {Object} namePartEl - The name part element to check.
 * @param {Object} currentTask - The current task object.
 * @returns {boolean} True if the dispatch is a remove dispatch for the name part, false otherwise.
 */

function isRemoveDispatchForNamePart(dispatch, namePartEl, currentTask) {
  return (
    (dispatch.type === 'REMOVE_ELEMENT' && dispatch.value.id === namePartEl?.id) ||
    (dispatch.type === 'REM_SUB' &&
      dispatch.value.subId === namePartEl?.id &&
      dispatch.value.superId === currentTask.id)
  )
}

export default assembleDispatches
